import React, { useState, useEffect } from "react";
import styled from "styled-components";
import { INewUser, AddUserInputProps } from "../../api/Interfaces";
import { ConfirmationPopupAnimation } from "../../views/private/animations";
import personAdd from "../../assets/svg/personAdd.svg";
import { HorizontalFlex, VerticalFlex, StyledButton, StyledInput, StyledSelect } from "../../views/private/styled";
import Overlay from "./Overlay";
import { Modal, Button } from "react-bootstrap";

const Container = styled(HorizontalFlex)`
  width: 100%;
  height: 100%;
  align-items: center;
  justify-content: center;
  position: relative;
`;

const AddUserIcon = styled.img`
  margin-left: 15px;
  width: 25px;
  height: 25px;
  transition: transform 0.3s ease;
  &:hover {
    transform: scale(1.2);
  }
`;

const UserInputContainer = styled(VerticalFlex)`
  position: relative;
  bottom: 3em;
  width: 35em;
  height: 45em;
  padding: 32px;
  border-radius: 16px;
  background: linear-gradient(145deg, #2a3444, #212936);
  box-shadow: 0 8px 20px rgba(0, 0, 0, 0.15);
  animation: ${ConfirmationPopupAnimation} 0.5s cubic-bezier(0.175, 0.885, 0.32, 1.275) 1;
  border: 1px solid rgba(255, 255, 255, 0.08);
`;

const Title = styled.h2`
  margin-top: 8px;
  color: #ffffff;
  font-size: 24px;
  font-weight: 600;
  letter-spacing: 0.5px;
`;

const CheckboxContainer = styled.div`
  display: flex;
  align-items: center;
  margin: 12px 0;
  width: 100%;
`;

const CheckboxInput = styled.input.attrs({ type: "checkbox" })`
  appearance: none;
  width: 18px;
  height: 18px;
  border: 2px solid #4b5e7a;
  border-radius: 4px;
  background-color: #2a3444;
  cursor: pointer;
  transition: all 0.2s ease;
  position: relative;

  &:checked {
    border-color: #14b57f;
    background-color: #14b57f;
  }

  &:checked::after {
    content: "✔";
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    color: #ffffff;
    font-size: 12px;
  }

  &:disabled {
    border-color: #4b5e7a;
    background-color: #3a3f4e;
    cursor: not-allowed;
    opacity: 0.6;
  }
`;

const CheckboxLabel = styled.label`
  margin-left: 10px;
  color: #d1d5db;
  font-size: 14px;
  font-weight: 400;
  cursor: pointer;
`;

const StyledButtonEnhanced = styled(StyledButton)`
  transition: all 0.3s ease;
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(20, 109, 93, 0.3);
  }
`;

const ErrorText = styled.div`
  color: #ff4d4f;
  font-size: 14px;
  margin-bottom: 10px;
  text-align: center;
`;

const AddUserInput = (props: AddUserInputProps & { admin_of_groups?: boolean }): React.ReactElement => {
  const { addUser, title, close, group, isSuperuser, currentUser, admin_of_groups = false } = props;
  const API_URL: string | undefined = process.env.REACT_APP_API_URL;

  const [name, setName] = useState<string>("");
  const [email, setEmail] = useState<string>("");
  const [isSuperuserField, setIsSuperuserField] = useState<boolean>(false);
  const [isAdmin, setIsAdmin] = useState<boolean>(admin_of_groups);
  const [isRegularUser, setIsRegularUser] = useState<boolean>(false);  const [isVerified, setIsVerified] = useState<boolean>(true);
  const [hasPointcloudAccess, setHasPointcloudAccess] = useState<boolean>(true);
  const [hasImageAccess, setHasImageAccess] = useState<boolean>(true);
  const [selectedGroup, setSelectedGroup] = useState<number | null>(group?.id || null);
  const [accessibleGroups, setAccessibleGroups] = useState<{ id: number; name: string }[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [showConfirmModal, setShowConfirmModal] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchAccessibleGroups = async () => {
      try {
        const res = await fetch(`${API_URL}/profile/accessible_groups`, {
          method: "GET",
          credentials: "include",
          headers: { "Content-Type": "application/json" },
        });
        if (res.ok) {
          const data: { id: number; name: string }[] = await res.json();
          const uniqueGroups = Array.from(
            new Map(data.map(group => [group.name, group])).values()
          );
          setAccessibleGroups(uniqueGroups);
          if (group && uniqueGroups.some(g => g.id === group.id)) {
            setSelectedGroup(group.id);
          } else if (!selectedGroup && uniqueGroups.length > 0) {
            setSelectedGroup(uniqueGroups[0].id);
          }
        } else if (res.status === 401) {
          setError("Not authenticated. Please log in.");
        } else if (res.status === 403) {
          setError("You do not have permission to view groups.");
        } else {
          setError("Failed to fetch groups. Please try again.");
        }
      } catch (err) {
        setError("An error occurred while fetching groups.");
      }
    };
    fetchAccessibleGroups();
  }, [API_URL, group, selectedGroup]);

const handleRegularUserChange = (checked: boolean) => {
    setIsRegularUser(checked);
    if (checked) {
      setIsAdmin(false);
      setIsSuperuserField(false);
    }
  };

  const handleAdminChange = (checked: boolean) => {
    setIsAdmin(checked);
    if (checked) {
      setIsRegularUser(false);
      setIsSuperuserField(false);
    }
  };

  const handleSuperuserChange = (checked: boolean) => {
    setIsSuperuserField(checked);
    if (checked) {
      setIsRegularUser(false);
      setIsAdmin(false);
    }
  };

  const handleAddUser = async () => {
    if (!name || !email) {
      setError("Name and email are required.");
      return;
    }

    if (!selectedGroup || selectedGroup <= 0) {
      setError("Please select a valid group.");
      return;
    }

    if (!hasPointcloudAccess && !hasImageAccess) {
      setError("User must have at least one type of access (Punktmoln or Bild).");
      return;
    }

    if (!isSuperuser && isSuperuserField) {
      setError("Only superusers can add superusers.");
      return;
    }

    if (!isSuperuser && isAdmin && !currentUser.admin_of_groups?.some(g => g.id === selectedGroup)) {
      setError("You can only add admins to groups you administer.");
      return;
    }

    const newUser: INewUser = {
      full_name: name,
      email: email,
      groups: isRegularUser && selectedGroup ? [selectedGroup] : [],
      admin_of_groups: isAdmin && selectedGroup ? [selectedGroup] : [],
      is_superuser: isSuperuserField,
      is_verified: isVerified,
      has_pointcloud_access: hasPointcloudAccess,
      has_image_access: hasImageAccess,
    };

    try {
      setIsLoading(true);
      await addUser(newUser, setIsLoading);
      close();
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : "Failed to add user. Please try again.";
      setError(errorMessage.includes("Permission denied") ? "You do not have permission to add users to this group." : errorMessage);
    } finally {
      setIsLoading(false);
      setShowConfirmModal(false);
    }
  };

  const handleConfirmAdd = () => {
    setShowConfirmModal(false);
    handleAddUser();
  };

  const handleCancelAdd = () => {
    setShowConfirmModal(false);
  };

  const addUserAndClose = () => {
    setShowConfirmModal(true);
  };

  const closeView = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    close();
  };

  const onKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === "Enter") {
      e.preventDefault();
      e.stopPropagation();
      addUserAndClose();
    }
  };

  useEffect(() => {
    const onKeyUp = (e: KeyboardEvent) => {
      if (e.key === "Escape") close();
    };
    document.addEventListener("keyup", onKeyUp, false);
    return () => document.removeEventListener("keyup", onKeyUp, false);
  }, [close]);

  return (
    <>
      <Overlay css={"z-index: 1000; cursor: auto; background: rgba(0, 0, 0, 0.7);"} onClick={closeView}>
        <Container>
          <UserInputContainer onClick={(e) => e.stopPropagation()} style={{ alignItems: "center" }}>
            <HorizontalFlex style={{ alignItems: "center", justifyContent: "center", width: "100%" }}>
              <Title>{title || (admin_of_groups ? "Add New Admin" : "Add New User")}</Title>
              <AddUserIcon src={personAdd} alt="Add User" />
            </HorizontalFlex>
            {error && <ErrorText>{error}</ErrorText>}
            <VerticalFlex style={{ alignItems: "center", width: "100%", gap: "12px" }}>
              <StyledInput
                type="text"
                placeholder="Full Name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                onKeyPress={onKeyPress}
                disabled={isLoading}
                style={{ borderRadius: "8px", padding: "12px" }}
              />
              <StyledInput
                type="email"
                placeholder="Email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                onKeyPress={onKeyPress}
                disabled={isLoading}
                style={{ borderRadius: "8px", padding: "12px" }}
              />
              <StyledSelect
                value={selectedGroup || ""}
                onChange={(e) => setSelectedGroup(Number(e.target.value) || null)}
                disabled={isLoading || (group && accessibleGroups.some(g => g.id === group.id))}
                style={{ borderRadius: "8px", padding: "12px", width: "100%" }}
              >
                {accessibleGroups.length === 0 ? (
                  <option value="" disabled>
                    No accessible groups
                  </option>
                ) : (
                  <>
                    <option value="" disabled>
                      Select group
                    </option>
                    {accessibleGroups.map((g) => (
                      <option key={g.id} value={g.id}>
                        {g.name}
                      </option>
                    ))}
                  </>
                )}
              </StyledSelect>
              <CheckboxContainer>
                <CheckboxInput
                  checked={isRegularUser}
                  onChange={(e) => handleRegularUserChange(e.target.checked)}
                  disabled={isLoading}
                />
                <CheckboxLabel>User</CheckboxLabel>
              </CheckboxContainer>
              <CheckboxContainer>
                <CheckboxInput
                  checked={isAdmin}
                  onChange={(e) => handleAdminChange(e.target.checked)}
                  disabled={isLoading || admin_of_groups}
                />
                <CheckboxLabel>Admin</CheckboxLabel>
              </CheckboxContainer>
              {isSuperuser && (
                <CheckboxContainer>
                  <CheckboxInput
                    checked={isSuperuserField}
                    onChange={(e) => handleSuperuserChange(e.target.checked)}
                    disabled={isLoading}
                  />
                  <CheckboxLabel>Superuser</CheckboxLabel>
                </CheckboxContainer>
              )}
              <CheckboxContainer>
                <CheckboxInput
                  checked={hasPointcloudAccess}
                  onChange={(e) => setHasPointcloudAccess(e.target.checked)}
                  disabled={isLoading}
                />
                <CheckboxLabel>Punktmoln (LIDAR) Access</CheckboxLabel>
              </CheckboxContainer>
              <CheckboxContainer>
                <CheckboxInput
                  checked={hasImageAccess}
                  onChange={(e) => setHasImageAccess(e.target.checked)}
                  disabled={isLoading}
                />
                <CheckboxLabel>Bild/Panorama Access</CheckboxLabel>
              </CheckboxContainer>
              <StyledButtonEnhanced
                onClick={!isLoading ? addUserAndClose : undefined}
                title={admin_of_groups ? "Add new admin" : "Add new user"}
                color={isLoading ? "gray" : "rgb(20, 109, 93)"}
                hoverColor={isLoading ? "gray" : "rgb(26, 135, 112)"}
                style={{
                  width: "70%",
                  cursor: isLoading ? "not-allowed" : "pointer",
                  opacity: isLoading ? 0.6 : 1,
                  borderRadius: "8px",
                  padding: "12px",
                  fontWeight: 500,
                }}
              >
                {isLoading ? "Adding..." : (admin_of_groups ? "Add Admin" : "Add User")}
              </StyledButtonEnhanced>
            </VerticalFlex>
          </UserInputContainer>
        </Container>
      </Overlay>

      <Modal show={showConfirmModal} onHide={handleCancelAdd} centered>
        <Modal.Header closeButton>
          <Modal.Title>Confirm Add User</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          Are you sure you want to add the user{" "}
          <strong>{name || email || "this user"}</strong>
          {isRegularUser ? " as a regular user" : ""}
          {isAdmin ? " as an admin" : ""}
          {isSuperuserField ? " as a superuser" : ""} to the group?
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={handleCancelAdd}>
            Cancel
          </Button>
          <Button
            variant="success"
            onClick={handleConfirmAdd}
            disabled={isLoading}
          >
            Confirm
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default AddUserInput;